import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Grid,
  Button,
  Container,
  TablePagination,
  TextField,
  InputAdornment,
  Alert,
  Tooltip,
  styled,
  Autocomplete,
  Chip,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import HistoryIcon from '@mui/icons-material/History';
import RefreshIcon from '@mui/icons-material/Refresh';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useNavigate, useLocation } from 'react-router-dom';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DateTimePicker } from '@mui/x-date-pickers';
import trLocale from 'date-fns/locale/tr';
import enLocale from 'date-fns/locale/en-US';
import * as XLSX from 'xlsx';
import { InventoryService } from './services/inventoryService';
import { PriceChangeLog } from './types/inventory.types';
import { useAuth } from '../../../contexts/AuthContext';
import AccountingDataTable, { DATATABLE_STYLES } from '../shared/AccountingDataTable';
import type { DataTableColumn } from '../shared/AccountingDataTable';
import { useTranslation } from 'react-i18next';

// Başlık alanı için styled component
const HeaderContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(3),
}));

const PRICE_TYPE_KEYS: { [key: string]: string } = {
  'purchasePrice': 'purchasePrice',
  'PurchasePrice': 'purchasePrice',
  'PURCHASE_PRICE': 'purchasePrice',
  'salePrice': 'salePrice',
  'SalePrice': 'salePrice',
  'SALE_PRICE': 'salePrice',
  'branchSalePrice': 'branchSalePrice',
  'BranchSalePrice': 'branchSalePrice',
  'BRANCH_SALE_PRICE': 'branchSalePrice',
  'branchPrice': 'branchPrice',
  'BranchPrice': 'branchPrice',
  'BRANCH_PRICE': 'branchPrice',
  'branchPurchasePrice': 'branchPurchasePrice',
  'BranchPurchasePrice': 'branchPurchasePrice',
  'BRANCH_PURCHASE_PRICE': 'branchPurchasePrice',
  'onlinePrice': 'onlinePrice',
  'OnlinePrice': 'onlinePrice',
  'ONLINE_PRICE': 'onlinePrice',
  'discountedPrice': 'discountedPrice',
  'DiscountedPrice': 'discountedPrice',
  'DISCOUNTED_PRICE': 'discountedPrice',
  'wholesalePrice': 'wholesalePrice',
  'WholesalePrice': 'wholesalePrice',
  'WHOLESALE_PRICE': 'wholesalePrice',
  'listPrice': 'listPrice',
  'ListPrice': 'listPrice',
  'LIST_PRICE': 'listPrice',
  'costPrice': 'costPrice',
  'CostPrice': 'costPrice',
  'COST_PRICE': 'costPrice',
};

const SOURCE_KEYS: { [key: string]: string } = {
  'INVENTORY': 'inventory',
  'Inventory': 'inventory',
  'inventory': 'inventory',
  'BRANCH_PRICE': 'branchPrice',
  'BranchPrice': 'branchPrice',
  'branchPrice': 'branchPrice',
  'BULK_UPDATE': 'bulkUpdate',
  'BulkUpdate': 'bulkUpdate',
  'bulkUpdate': 'bulkUpdate',
  'IMPORT': 'import',
  'Import': 'import',
  'import': 'import',
  'API': 'api',
  'api': 'api',
  'ECOMMERCE': 'ecommerce',
  'Ecommerce': 'ecommerce',
  'ecommerce': 'ecommerce',
  'MOBILE': 'mobile',
  'Mobile': 'mobile',
  'mobile': 'mobile',
  'SYNC': 'sync',
  'Sync': 'sync',
  'sync': 'sync',
  'MANUAL': 'manual',
  'Manual': 'manual',
  'manual': 'manual',
  'WEB': 'web',
  'Web': 'web',
  'web': 'web',
};

// Fiyat türü renkler
const PRICE_TYPE_COLORS: { [key: string]: 'primary' | 'secondary' | 'success' | 'warning' | 'info' | 'error' } = {
  'purchasePrice': 'info',
  'PurchasePrice': 'info',
  'PURCHASE_PRICE': 'info',
  'salePrice': 'primary',
  'SalePrice': 'primary',
  'SALE_PRICE': 'primary',
  'branchSalePrice': 'secondary',
  'BranchSalePrice': 'secondary',
  'BRANCH_SALE_PRICE': 'secondary',
  'branchPrice': 'secondary',
  'BranchPrice': 'secondary',
  'BRANCH_PRICE': 'secondary',
  'branchPurchasePrice': 'warning',
  'BranchPurchasePrice': 'warning',
  'BRANCH_PURCHASE_PRICE': 'warning',
  'onlinePrice': 'success',
  'OnlinePrice': 'success',
  'ONLINE_PRICE': 'success',
  'discountedPrice': 'error',
  'DiscountedPrice': 'error',
  'DISCOUNTED_PRICE': 'error',
  'wholesalePrice': 'primary',
  'WholesalePrice': 'primary',
  'WHOLESALE_PRICE': 'primary',
  'listPrice': 'info',
  'ListPrice': 'info',
  'LIST_PRICE': 'info',
  'costPrice': 'warning',
  'CostPrice': 'warning',
  'COST_PRICE': 'warning',
};

const PriceChangeLogs: React.FC = () => {
  const { t, i18n } = useTranslation('accounting');
  const locale = i18n.language?.toLowerCase().startsWith('en') ? 'en-US' : 'tr-TR';
  const pickerLocale = i18n.language?.toLowerCase().startsWith('en') ? enLocale : trLocale;
  const navigate = useNavigate();
  const location = useLocation();
  const { userDetails } = useAuth();
  
  // URL'den veya state'den companyId al
  const searchParams = new URLSearchParams(location.search);
  const companyIdFromUrl = searchParams.get('companyId');
  const itemIdFromUrl = searchParams.get('itemId');
  const itemNameFromUrl = searchParams.get('itemName');
  
  // State değişkenleri
  const [loading, setLoading] = useState<boolean>(true);
  const [logs, setLogs] = useState<PriceChangeLog[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(25);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [priceTypeFilter, setPriceTypeFilter] = useState<string>('all');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [sortColumn, setSortColumn] = useState<string>('createdAt');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [showInfoBox, setShowInfoBox] = useState<boolean>(false);

  // companyId: URL'den veya AuthContext'ten al
  const companyId = companyIdFromUrl || userDetails?.companyId || null;

  // companyId yoksa hata göster
  useEffect(() => {
    if (!companyId) {
      setError(t('stock.priceChangeLogs.errors.companyNotFound'));
      setLoading(false);
    } else {
      setError(null);
    }
  }, [companyId]);

  // Logları yükle - Server-side pagination ve filtreleme
  const fetchLogs = async () => {
    if (!companyId) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Server-side filtreleme ve sayfalama
      const response = await InventoryService.getPriceChangeLogs(
        companyId,
        itemIdFromUrl || '', // Boş string olursa tüm loglar gelir
        page + 1,
        rowsPerPage,
        {
          searchTerm: debouncedSearchTerm,
          priceType: priceTypeFilter,
          startDate,
          endDate,
          sortColumn,
          sortDirection
        }
      );
      
      setLogs(response.data);
      setTotalCount(response.totalCount);
    } catch (err: any) {
      console.error('Fiyat değişim logları yüklenirken hata:', err);
      setError(err.message || t('stock.priceChangeLogs.errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  // Debounced search term
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>('');
  
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setPage(0); // Arama değişince ilk sayfaya dön
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Filtre değişikliklerinde sayfa sıfırla
  useEffect(() => {
    setPage(0);
  }, [priceTypeFilter, startDate, endDate, sortColumn, sortDirection]);

  // Server'dan veri çek
  useEffect(() => {
    if (companyId) {
      fetchLogs();
    }
  }, [companyId, itemIdFromUrl, page, rowsPerPage, debouncedSearchTerm, priceTypeFilter, startDate, endDate, sortColumn, sortDirection]);

  // Sayfa değiştir
  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  // Sayfa başına satır sayısını değiştir
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Sıralama değiştir
  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('desc');
    }
  };

  // Fiyat değişim ikonu
  const getPriceChangeIcon = (oldValue: number, newValue: number) => {
    if (newValue > oldValue) {
      return <TrendingUpIcon color="success" fontSize="small" />;
    } else if (newValue < oldValue) {
      return <TrendingDownIcon color="error" fontSize="small" />;
    }
    return <TrendingFlatIcon color="disabled" fontSize="small" />;
  };

  // Fiyat değişim yüzdesi
  const getPriceChangePercent = (oldValue: number, newValue: number): string => {
    if (oldValue === 0) return newValue > 0 ? '+∞%' : '0%';
    const percent = ((newValue - oldValue) / oldValue) * 100;
    return `${percent > 0 ? '+' : ''}${percent.toFixed(1)}%`;
  };

  // Para formatla
  const formatCurrency = (value: number, currency: string = 'TRY'): string => {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency || 'TRY',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  // Tarih formatla
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleString(locale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPriceTypeName = (priceType: string) => {
    const key = PRICE_TYPE_KEYS[priceType];
    return key ? t(`stock.priceChangeLogs.priceTypes.${key}`) : priceType;
  };

  const getSourceName = (source?: string) => {
    if (!source) return '-';
    const key = SOURCE_KEYS[source];
    return key ? t(`stock.priceChangeLogs.sources.${key}`) : source;
  };

  // Excel'e aktar
  const handleExportToExcel = () => {
    const exportData = logs.map(log => ({
      [t('stock.priceChangeLogs.columns.date')]: formatDate(log.createdAt),
      [t('stock.priceChangeLogs.columns.productName')]: log.productName || '',
      [t('stock.priceChangeLogs.columns.stockCode')]: log.stockCode || '',
      [t('stock.priceChangeLogs.columns.branch')]: log.branchName || t('stock.priceChangeLogs.general'),
      [t('stock.priceChangeLogs.columns.priceType')]: getPriceTypeName(log.priceType),
      [t('stock.priceChangeLogs.columns.oldPrice')]: log.oldValue || 0,
      [t('stock.priceChangeLogs.columns.newPrice')]: log.newValue || 0,
      [t('stock.priceChangeLogs.columns.changePercent')]: getPriceChangePercent(log.oldValue || 0, log.newValue || 0),
      [t('stock.priceChangeLogs.columns.currency')]: log.currency || 'TRY',
      [t('stock.priceChangeLogs.columns.user')]: log.userName || t('stock.priceChangeLogs.unknownUser'),
      [t('stock.priceChangeLogs.columns.source')]: getSourceName(log.source),
      [t('stock.priceChangeLogs.columns.notes')]: log.notes || ''
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, t('stock.priceChangeLogs.title'));
    
    const fileName = `price_change_logs_${new Date().toISOString().slice(0, 10)}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  // Benzersiz fiyat türleri - PRICE_TYPE_NAMES'den al
  const uniquePriceTypes = Object.keys(PRICE_TYPE_KEYS).filter((key, index, self) => {
    // Sadece bir varyasyonu al (örn: SALE_PRICE, SalePrice, salePrice -> sadece biri)
    const normalizedKey = key.toUpperCase().replace(/_/g, '');
    const firstIndex = self.findIndex(k => k.toUpperCase().replace(/_/g, '') === normalizedKey);
    return firstIndex === index;
  });

  // ─── Kolon Tanımları ───────────────────────────────
  const priceLogColumns = useMemo<DataTableColumn<PriceChangeLog>[]>(() => [
    {
      id: 'createdAt',
      label: t('stock.priceChangeLogs.columns.date'),
      field: 'createdAt' as keyof PriceChangeLog,
      sortable: true,
      width: '140px',
      render: (log) => (
        <Typography variant="body2">{formatDate(log.createdAt)}</Typography>
      ),
    },
    {
      id: 'productName',
      label: t('stock.priceChangeLogs.columns.product'),
      field: 'productName' as keyof PriceChangeLog,
      sortable: true,
      render: (log) => (
        <>
          <Typography variant="body2" fontWeight="medium">{log.productName}</Typography>
          <Typography variant="caption" color="text.secondary">{log.stockCode}</Typography>
        </>
      ),
    },
    {
      id: 'branchName',
      label: t('stock.priceChangeLogs.columns.branch'),
      render: (log) => (
        <Typography variant="body2">{log.branchName || t('stock.priceChangeLogs.general')}</Typography>
      ),
    },
    {
      id: 'priceType',
      label: t('stock.priceChangeLogs.columns.priceType'),
      field: 'priceType' as keyof PriceChangeLog,
      sortable: true,
      render: (log) => (
        <Chip
          label={getPriceTypeName(log.priceType)}
          size="small"
          color={PRICE_TYPE_COLORS[log.priceType] || 'default'}
          variant="outlined"
        />
      ),
    },
    {
      id: 'oldValue',
      label: t('stock.priceChangeLogs.columns.oldPrice'),
      field: 'oldValue' as keyof PriceChangeLog,
      sortable: true,
      align: 'right',
      render: (log) => (
        <Typography variant="body2">{formatCurrency(log.oldValue || 0, log.currency)}</Typography>
      ),
    },
    {
      id: 'newValue',
      label: t('stock.priceChangeLogs.columns.newPrice'),
      field: 'newValue' as keyof PriceChangeLog,
      sortable: true,
      align: 'right',
      render: (log) => {
        const isIncrease = (log.newValue || 0) > (log.oldValue || 0);
        const isDecrease = (log.newValue || 0) < (log.oldValue || 0);
        return (
          <Typography
            variant="body2"
            fontWeight="medium"
            color={isIncrease ? 'success.main' : isDecrease ? 'error.main' : 'inherit'}
          >
            {formatCurrency(log.newValue || 0, log.currency)}
          </Typography>
        );
      },
    },
    {
      id: 'change',
      label: t('stock.priceChangeLogs.columns.change'),
      align: 'center',
      render: (log) => {
        const oldVal = log.oldValue || 0;
        const newVal = log.newValue || 0;
        const isIncrease = newVal > oldVal;
        const isDecrease = newVal < oldVal;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
            {getPriceChangeIcon(oldVal, newVal)}
            <Typography
              variant="caption"
              fontWeight="medium"
              color={isIncrease ? 'success.main' : isDecrease ? 'error.main' : 'text.secondary'}
            >
              {getPriceChangePercent(oldVal, newVal)}
            </Typography>
          </Box>
        );
      },
    },
    {
      id: 'userName',
      label: t('stock.priceChangeLogs.columns.user'),
      field: 'userName' as keyof PriceChangeLog,
      sortable: true,
      render: (log) => (
        <Typography variant="body2">{log.userName || t('stock.priceChangeLogs.unknownUser')}</Typography>
      ),
    },
    {
      id: 'source',
      label: t('stock.priceChangeLogs.columns.source'),
      render: (log) => (
        <Typography variant="caption" color="text.secondary">
          {getSourceName(log.source)}
        </Typography>
      ),
    },
  ], [t, locale]);

  // ─── Koşullu Satır Renklendirme ───────────────────
  const getLogRowSx = useCallback((log: PriceChangeLog) => {
    const oldVal = log.oldValue || 0;
    const newVal = log.newValue || 0;
    const isIncrease = newVal > oldVal;
    const isDecrease = newVal < oldVal;
    return {
      backgroundColor: isIncrease
        ? 'rgba(76, 175, 80, 0.05)'
        : isDecrease
          ? 'rgba(244, 67, 54, 0.05)'
          : 'inherit'
    };
  }, []);

  return (
    <Container maxWidth="xl" sx={{ mt: 3, mb: 3 }}>
      {/* Başlık */}
      <HeaderContainer>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/stok')}
          >
            {t('stock.priceChangeLogs.actions.back')}
          </Button>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HistoryIcon fontSize="large" color="primary" />
            <Box>
              <Typography variant="h5" fontWeight="bold">
                {t('stock.priceChangeLogs.title')}
              </Typography>
              {itemNameFromUrl && (
                <Typography variant="body2" color="text.secondary">
                  {itemNameFromUrl}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title={t('stock.priceChangeLogs.actions.info')}>
            <IconButton onClick={() => setShowInfoBox(prev => !prev)}>
              <InfoOutlinedIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('stock.priceChangeLogs.actions.refresh')}>
            <IconButton onClick={fetchLogs} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="outlined"
            startIcon={<FileDownloadIcon />}
            onClick={handleExportToExcel}
            disabled={logs.length === 0}
          >
            {t('stock.priceChangeLogs.actions.downloadExcel')}
          </Button>
        </Box>
      </HeaderContainer>

      {showInfoBox && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5 }}>
            {t('stock.priceChangeLogs.infoBox.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            {t('stock.priceChangeLogs.infoBox.description')}
          </Typography>
          <Typography variant="body2">
            {t('stock.priceChangeLogs.infoBox.item1')}
          </Typography>
          <Typography variant="body2">
            {t('stock.priceChangeLogs.infoBox.item2')}
          </Typography>
          <Typography variant="body2">
            {t('stock.priceChangeLogs.infoBox.item3')}
          </Typography>
        </Alert>
      )}

      {/* Filtreler */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              placeholder={t('stock.priceChangeLogs.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('stock.priceChangeLogs.filters.priceType')}</InputLabel>
              <Select
                value={priceTypeFilter}
                label={t('stock.priceChangeLogs.filters.priceType')}
                onChange={(e) => setPriceTypeFilter(e.target.value)}
              >
                <MenuItem value="all">{t('stock.priceChangeLogs.filters.all')}</MenuItem>
                {uniquePriceTypes.map(type => (
                  <MenuItem key={type} value={type}>
                    {getPriceTypeName(type)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2.5}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={pickerLocale}>
              <DateTimePicker
                label={t('stock.priceChangeLogs.filters.startDate')}
                value={startDate}
                onChange={(date) => setStartDate(date)}
                slotProps={{
                  textField: {
                    size: 'small',
                    fullWidth: true
                  }
                }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} md={2.5}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={pickerLocale}>
              <DateTimePicker
                label={t('stock.priceChangeLogs.filters.endDate')}
                value={endDate}
                onChange={(date) => setEndDate(date)}
                slotProps={{
                  textField: {
                    size: 'small',
                    fullWidth: true
                  }
                }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              variant="text"
              onClick={() => {
                setSearchTerm('');
                setPriceTypeFilter('all');
                setStartDate(null);
                setEndDate(null);
              }}
            >
              {t('stock.priceChangeLogs.actions.clearFilters')}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Hata mesajı */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Tablo */}
      <AccountingDataTable<PriceChangeLog>
        columns={priceLogColumns}
        rows={logs}
        getRowId={(log) => log.id}
        loading={loading}
        pagination={false}
        onSort={(field, direction) => {
          setSortColumn(field);
          setSortDirection(direction);
        }}
        sortField={sortColumn}
        sortDirection={sortDirection}
        getRowSx={getLogRowSx}
        emptyMessage={t('stock.priceChangeLogs.empty')}
        ariaLabel={t('stock.priceChangeLogs.tableAriaLabel')}
        maxHeight="calc(100vh - 450px)"
        columnResizeStorageKey="price-change-logs"
      />
      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[25, 50, 100]}
        labelRowsPerPage={t('stock.pagination.rowsPerPage')}
        labelDisplayedRows={({ from, to, count }) => t('stock.pagination.displayedRows', { from, to, count })}
        sx={DATATABLE_STYLES.pagination}
      />
    </Container>
  );
};

export default PriceChangeLogs;
